import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getUserOrganizations } from '@/lib/firebase/queries/organizations';
import { Building2, Mail, Phone, Globe, LogOut, Loader2 } from 'lucide-react';
import { doc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { toast } from 'sonner';
import type { Organization } from '@/types/organization';

export function OrganizationSettings() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [leavingOrg, setLeavingOrg] = useState<string | null>(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [selectedOrgToLeave, setSelectedOrgToLeave] = useState<Organization | null>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const orgs = await getUserOrganizations(user.uid);
        
        // Remove duplicates and collect roles for each organization
        const orgMap = new Map();
        orgs.forEach(org => {
          if (orgMap.has(org.id)) {
            // Organization already exists, add role if not already present
            const existingOrg = orgMap.get(org.id);
            if (org.role && !existingOrg.roles.includes(org.role)) {
              existingOrg.roles.push(org.role);
            }
          } else {
            // New organization, create entry with roles array
            orgMap.set(org.id, {
              ...org,
              roles: org.role ? [org.role] : []
            });
          }
        });
        
        const uniqueOrgs = Array.from(orgMap.values());
        
        setOrganizations(uniqueOrgs);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [user]);

  const handleLeaveOrganization = (organization: Organization) => {
    setSelectedOrgToLeave(organization);
    setShowLeaveDialog(true);
  };

  const confirmLeaveOrganization = async () => {
    if (!selectedOrgToLeave || !user) return;

    try {
      setLeavingOrg(selectedOrgToLeave.id);
      
      // Find and delete user's membership records for this organization
      const membershipQuery = query(
        collection(db, 'organizationMembers'),
        where('userId', '==', user.uid),
        where('organizationId', '==', selectedOrgToLeave.id)
      );
      
      const membershipSnapshot = await getDocs(membershipQuery);
      
      // Delete all membership records for this user in this organization
      const deletePromises = membershipSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Update local state to remove the organization
      setOrganizations(prev => prev.filter(org => org.id !== selectedOrgToLeave.id));
      
      toast.success(
        language === 'ar' 
          ? `تم ترك ${selectedOrgToLeave.name} بنجاح`
          : `Successfully left ${selectedOrgToLeave.nameEn || selectedOrgToLeave.name}`
      );
      
      setShowLeaveDialog(false);
      setSelectedOrgToLeave(null);
    } catch (error) {
      console.error('Error leaving organization:', error);
      toast.error(
        language === 'ar' 
          ? 'فشل في ترك المنظمة'
          : 'Failed to leave organization'
      );
    } finally {
      setLeavingOrg(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('settings.noOrganizations')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {t('settings.myOrganizations')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {organizations.map((organization) => (
              <div 
                key={organization.id}
                className="p-4 rounded-lg border bg-card/50 space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Organization Logo */}
                    {organization.logoUrl ? (
                      <div className="flex-shrink-0">
                        <img 
                          src={organization.logoUrl} 
                          alt={organization.name}
                          className="h-10 w-auto max-w-[60px] object-contain rounded"
                        />
                      </div>
                    ) : (
                      <div className="flex-shrink-0 h-10 w-10 bg-muted rounded flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                    )}
                    
                    {/* Organization Name */}
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-lg block">
                        {language === 'ar' 
                          ? organization.name 
                          : (organization.nameEn || organization.name)
                        }
                      </span>
                      {organization.nameEn && organization.nameEn !== organization.name && language === 'ar' && (
                        <span className="text-sm text-muted-foreground block">
                          {organization.nameEn}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Leave Organization Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleLeaveOrganization(organization)}
                    disabled={leavingOrg === organization.id}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300 flex-shrink-0"
                  >
                    {leavingOrg === organization.id ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {language === 'ar' ? 'جاري الترك...' : 'Leaving...'}
                      </>
                    ) : (
                      <>
                        <LogOut className="h-4 w-4 mr-2" />
                        {language === 'ar' ? 'ترك المنظمة' : 'Leave Organization'}
                      </>
                    )}
                  </Button>
                </div>
                
                <div className="pl-3 space-y-2">
                  {organization.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{organization.email}</span>
                    </div>
                  )}
                  {organization.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{organization.phone}</span>
                    </div>
                  )}
                  {organization.website && (
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <a 
                        href={organization.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {organization.website}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-sm capitalize">{organization.type}</span>
                  </div>
                  
                  {organization.roles && organization.roles.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{t('settings.myRoles')}:</span>
                      <div className="flex flex-wrap gap-1">
                        {organization.roles.map((role, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-md capitalize"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Leave Organization Confirmation Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'ar' ? 'ترك المنظمة' : 'Leave Organization'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'ar' 
                ? `هل أنت متأكد من أنك تريد ترك "${selectedOrgToLeave?.name}"؟ لن تتمكن من الوصول إلى موارد هذه المنظمة بعد الآن.`
                : `Are you sure you want to leave "${selectedOrgToLeave?.nameEn || selectedOrgToLeave?.name}"? You will no longer have access to this organization's resources.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLeaveOrganization}
              className="bg-red-600 hover:bg-red-700"
            >
              {language === 'ar' ? 'ترك المنظمة' : 'Leave Organization'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 